/**
 * Trial-unit business rules — which radios count as a trial radio at all,
 * and the client-side status derived from a trial radio's dates. Shared by
 * the Trial Units dashboard's stat cards, its grid's status column, and the
 * stat cards' click-to-filter mapping onto the grid's date-range filter
 * params, so all three agree on the same rules.
 *
 * Status is derived from the list row's flat billing_trial_start_date /
 * billing_trial_end_date fields (nullable ISO 'yyyy-MM-dd' strings — the
 * backing model fields are DateFields, so there is no time component to
 * account for). An API that hasn't landed those fields yet sends both as
 * null, which resolves to the UNKNOWN status rather than throwing.
 *
 * Contains:
 * - TRIAL_SCOPE_PARAMS    — base filter selecting trial radios (both dates set)
 * - DATE_PARAM_FORMAT     — the wire date format shared with the filter params
 * - EXPIRING_WINDOW_DAYS  — days-until-end threshold for the "expiring" status
 * - TRIAL_STATUS          — status code enum
 * - TRIAL_STATUS_CARD_ORDER — the four countable statuses, in stat-card order
 * - TRIAL_STATUS_CHIPS    — status -> { color, cssVar, labelKey, fallback }
 * - deriveTrialStatus     — pure (startDate, endDate, today?) -> status code
 * - trialStatusDateFilter — status -> odl-app date-range filter params
 *
 * Returns plain JS — no JSX.
 */
import { addDays, format, subDays } from 'date-fns';

// Only radios with both trial dates recorded — fixed scope, not user-togglable.
export const TRIAL_SCOPE_PARAMS = {
  billing_trial_start_date_isnull: false,
  billing_trial_end_date_isnull: false,
};

export const DATE_PARAM_FORMAT = 'yyyy-MM-dd';

export const EXPIRING_WINDOW_DAYS = 30;

export const TRIAL_STATUS = Object.freeze({
  SCHEDULED: 'scheduled',
  ACTIVE: 'active',
  EXPIRING: 'expiring',
  ENDED: 'ended',
  UNKNOWN: 'unknown',
});

// UNKNOWN has no card — a null date pair is the degraded/pre-contract case,
// not a trial state worth counting.
export const TRIAL_STATUS_CARD_ORDER = [
  TRIAL_STATUS.ACTIVE,
  TRIAL_STATUS.EXPIRING,
  TRIAL_STATUS.SCHEDULED,
  TRIAL_STATUS.ENDED,
];

export const TRIAL_STATUS_CHIPS = {
  [TRIAL_STATUS.SCHEDULED]: {
    color: 'info',
    cssVar: 'var(--status-info)',
    labelKey: 'billing.trialUnits.statusScheduled',
    fallback: 'Scheduled',
  },
  [TRIAL_STATUS.ACTIVE]: {
    color: 'ok',
    cssVar: 'var(--status-ok)',
    labelKey: 'billing.trialUnits.statusActive',
    fallback: 'Active',
  },
  [TRIAL_STATUS.EXPIRING]: {
    color: 'warning',
    cssVar: 'var(--status-warning)',
    labelKey: 'billing.trialUnits.statusExpiring',
    fallback: 'Expiring',
  },
  [TRIAL_STATUS.ENDED]: {
    color: 'neutral',
    cssVar: 'var(--status-neutral)',
    labelKey: 'billing.trialUnits.statusEnded',
    fallback: 'Ended',
  },
  [TRIAL_STATUS.UNKNOWN]: {
    color: 'neutral',
    cssVar: 'var(--status-neutral)',
    labelKey: 'billing.notAvailable',
    fallback: 'Not Available',
  },
};

/**
 * @param {string|null} startDate - billing_trial_start_date, 'yyyy-MM-dd' or null.
 * @param {string|null} endDate - billing_trial_end_date, 'yyyy-MM-dd' or null.
 * @param {Date} [today] - reference date; defaults to now, injectable for tests.
 * @returns {string} One of TRIAL_STATUS's values.
 */
export function deriveTrialStatus(startDate, endDate, today = new Date()) {
  if (!startDate || !endDate) return TRIAL_STATUS.UNKNOWN;
  // Both fields are date-only strings, so lexicographic comparison against
  // another 'yyyy-MM-dd' string is equivalent to chronological comparison.
  const todayStr = format(today, DATE_PARAM_FORMAT);
  if (startDate > todayStr) return TRIAL_STATUS.SCHEDULED;
  if (endDate < todayStr) return TRIAL_STATUS.ENDED;
  const windowEndStr = format(addDays(today, EXPIRING_WINDOW_DAYS), DATE_PARAM_FORMAT);
  return endDate <= windowEndStr ? TRIAL_STATUS.EXPIRING : TRIAL_STATUS.ACTIVE;
}

/**
 * Maps a trial status to the odl-app date-range filter params that select
 * the same radios server-side, for the stat cards' click-to-filter behavior.
 *
 * @param {string} status - a TRIAL_STATUS value.
 * @param {Date} [today]
 * @returns {Object|null} Filter params, or null for UNKNOWN (a null date
 *   pair, which no date-range filter can select).
 */
export function trialStatusDateFilter(status, today = new Date()) {
  const todayStr = format(today, DATE_PARAM_FORMAT);
  if (status === TRIAL_STATUS.SCHEDULED) {
    return { 'billing__trial_start_date.gte': format(addDays(today, 1), DATE_PARAM_FORMAT) };
  }
  if (status === TRIAL_STATUS.ENDED) {
    return { 'billing__trial_end_date.lte': format(subDays(today, 1), DATE_PARAM_FORMAT) };
  }
  if (status === TRIAL_STATUS.ACTIVE) {
    return {
      'billing__trial_start_date.lte': todayStr,
      // First day past the expiring window: partitions ACTIVE/EXPIRING the
      // same way deriveTrialStatus does (end <= window end → EXPIRING).
      'billing__trial_end_date.gte': format(addDays(today, EXPIRING_WINDOW_DAYS + 1), DATE_PARAM_FORMAT),
    };
  }
  if (status === TRIAL_STATUS.EXPIRING) {
    return {
      'billing__trial_start_date.lte': todayStr,
      'billing__trial_end_date.gte': todayStr,
      'billing__trial_end_date.lte': format(addDays(today, EXPIRING_WINDOW_DAYS), DATE_PARAM_FORMAT),
    };
  }
  return null;
}
